import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

/**
 * Reusable FAQ section with accordion UI.
 * Pair with <Seo jsonLd={faqJsonLd(items)}/> on the same page for SEO.
 */
export default function FaqSection({
  eyebrow = "Frequently asked",
  title = "Questions families ask us most",
  description,
  items = [],
  testIdPrefix = "faq",
}) {
  if (!items.length) return null;
  return (
    <section className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid={`${testIdPrefix}-section`}>
      <div className="mb-10 md:mb-14">
        {eyebrow && (
          <div className="text-sm text-[#9CAF88] font-semibold mb-3 uppercase tracking-wider">{eyebrow}</div>
        )}
        <h2 className="font-display text-3xl md:text-4xl font-semibold text-[#1A2E24] tracking-tight max-w-2xl">
          {title}
        </h2>
        {description && (
          <p className="mt-4 text-[#5C6B62] leading-relaxed max-w-2xl">{description}</p>
        )}
      </div>

      <Accordion type="single" collapsible className="space-y-3">
        {items.map((item, idx) => (
          <AccordionItem
            key={idx}
            value={`item-${idx}`}
            className="rounded-2xl border border-[#E2DFD8] bg-white px-5 md:px-7 data-[state=open]:shadow-[0_8px_30px_rgba(44,76,59,0.06)]"
            data-testid={`${testIdPrefix}-item-${idx}`}
          >
            <AccordionTrigger className="text-left font-display text-base md:text-lg font-semibold text-[#1A2E24] hover:no-underline py-5">
              {item.q}
            </AccordionTrigger>
            <AccordionContent className="text-[#5C6B62] text-base leading-relaxed pb-5">
              {item.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

/** Build a FAQPage JSON-LD object from items. */
export function faqJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: it.a,
      },
    })),
  };
}
